import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../api/client";
import type { MeterProviderInfo, MeterReadingEntry } from "../types/meter";
import { extractErrorMessage } from "../utils/error";

const useMeterProviders = () => {
  const { t } = useTranslation(["common", "meterReadings"]);
  const [providers, setProviders] = useState<MeterProviderInfo[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProviders = useCallback(async () => {
    try {
      const list = await api.meterReadings.providers();
      setProviders(list);
      setSelectedProviderId((current) => current ?? list[0]?.id ?? null);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, t("meterReadings:settings.loadProvidersError")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadProviders();
  }, [loadProviders]);

  const selectedProvider = providers.find((p) => p.id === selectedProviderId) ?? null;

  return { providers, selectedProviderId, selectedProvider, setSelectedProviderId, error, loadingProviders: loading };
};

const useMeterHistory = (providerId: number | null) => {
  const [history, setHistory] = useState<MeterReadingEntry[]>([]);

  const loadHistory = useCallback(async () => {
    if (!providerId) return;
    try {
      setHistory(await api.meterReadings.list(providerId));
    } catch (err: unknown) {
      console.error("Failed to load meter reading history", err);
    }
  }, [providerId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  return { history, reloadHistory: loadHistory };
};

export { useMeterProviders, useMeterHistory };
